<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";

import { usePopupManager } from "@/shared/composables/usePopupManager";

import RootAlertLayout from "@/shared/components/popup-layout/RootAlertLayout.vue";
import useCheckMouseInitialClick from "@/shared/components/temparary/components/modals/useCheckMouseInitialClick";

import type { BasePopupProps } from "@/shared/plugins/interface";

const props = defineProps<BasePopupProps>();

// modal 의 body 부분을 ref 로 참조합니다.
const dimRef = ref<HTMLDivElement | null>(null);
// 마우스의 누름과 띄움에 따른 이벤트를 실행하고자 호출합니다.
const { mount, unmount, isInitialClickInside } = useCheckMouseInitialClick(dimRef);
const { closePopup } = usePopupManager();

/**
 * @description 모달을 닫는 함수입니다. 이 함수의 실행 시점은 mousedown 이벤트에 따른 콜백함수가 실행된 이후에 실행이 됩니다.
 */
const closeModal = () => {
    // mousedown 이 발생한 위치가 모달의 내부영역이라면, 모달을 닫지 않습니다.
    if (isInitialClickInside.value) {
        return;
    }

    // 외부에서 닫히면 안되는 팝업일 경우 그대로 return
    if (props.rest.isStrong) {
        return;
    }

    props.rest.closeCallback?.();
    closePopup(props.closeKey ?? "");
};

onMounted(() => {
    // 해당 modal 이 렌더링 되었다면 마우스 이벤트를 등록합니다. (addEventListner)
    if (dimRef.value) {
        mount();
    }
});

onUnmounted(() => {
    // 해당 modal 이 사라졌다면 마우스 이벤트를 제거합니다. (removeEventListner)
    unmount();
});
</script>

<template>
    <div
        class="flex fixed top-0 left-0 justify-center items-center w-full xl:h-full h-lvh z-[110] pointer-events-auto"
    >
        <div class="absolute inset-0 bg-primary-black/30" @click="closeModal"></div>

        <!-- Modal content with transform + fade -->
        <Transition name="popup-fade" appear>
            <div
                class="flex relative justify-center items-center h-auto w-fit"
                tabindex="-1"
                ref="dimRef"
            >
                <RootAlertLayout>
                    <slot v-bind="props" />
                </RootAlertLayout>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
/* popup transitions */
.popup-fade-enter-active,
.popup-fade-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none; /* 트랜지션 중에는 클릭 이벤트 방지 */
}

.popup-fade-enter-from,
.popup-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.popup-fade-enter-to,
.popup-fade-leave-from {
    opacity: 1;
    transform: translateY(0);
}

.popup-fade-move {
    transition: transform 0.5s ease;
}
</style>
